<template>
  <div class="plotly"></div>
</template>

<script>
import Plotly from 'plotly.js/lib/index-basic'
import moment from '@/utils/moment'
import _ from 'lodash'
import { DISPLAY_DATE_HOUR, MONTH_START_OF_YEAR_HYDROLOGICAL } from '@/utils/date'

// Injection de la langue
Plotly.register({
  moduleType: 'locale',
  name: 'fr-CH',
  dictionary: {
    Autoscale: 'Échelle automatique',
    'Box Select': 'Sélection rectangulaire',
    'Click to enter Colorscale titre': 'Ajouter un titre à l’échelle de couleurs',
    'Click to enter Component A titre': 'Ajouter un titre à la composante A',
    'Click to enter Component B titre': 'Ajouter un titre à la composante B',
    'Click to enter Component C titre': 'Ajouter un titre à la composante C',
    'Click to enter Plot titre': 'Ajouter un titre au graphique',
    'Click to enter X axis titre': 'Ajouter un titre à l’axe des x',
    'Click to enter Y axis titre': 'Ajouter un titre à l’axe des y',
    'Click to enter radial axis title': 'Ajouter un titre à l’axe radial',
    'Compare data on hover': 'Comparaison entre données en survol',
    'Double-click on legend to isolate one trace': 'Double-cliquer sur la légende pour isoler une série',
    'Double-click to zoom back out': 'Double-cliquer pour dézoomer',
    'Download plot as a png': 'Télécharger le graphique en fichier PNG',
    'Edit in Chart Studio': 'Éditer le graphique sur plot.ly',
    'IE only supports svg.  Changing format to svg.': 'IE ne permet que les conversions en SVG. Conversion en SVG en cours.',
    'Lasso Select': 'Sélection lasso',
    'Orbital rotation': 'Rotation orbitale',
    Pan: 'Translation',
    'Produced with Plotly': 'Généré avec Plotly',
    Reset: 'Réinitialiser',
    'Reset axes': 'Réinitialiser les axes',
    'Reset camera to default': 'Régler la caméra à sa valeur défaut',
    'Reset camera to last save': 'Régler la caméra à sa valeur sauvegardée',
    'Reset view': 'Réinitialiser',
    'Reset views': 'Réinitialiser',
    'Show closest data on hover': 'Données les plus proches en survol',
    'Snapshot succeeded': 'Conversion réussie',
    'Sorry, there was a problem downloading your snapshot!': 'Désolé, un problème est survenu lors du téléchargement de votre graphique\xa0!',
    'Taking snapshot - this may take a few seconds': 'Conversion en cours, ceci peut prendre quelques secondes',
    Zoom: 'Zoom',
    'Zoom in': 'Zoom intérieur',
    'Zoom out': 'Zoom extérieur',
    'close:': 'fermeture\xa0:',
    trace: 'série',
    'lat:': 'lat.\xa0:',
    'lon:': 'lon.\xa0:',
    'q1:': 'q1\xa0:',
    'q3:': 'q3\xa0:',
    'source:': 'source\xa0:',
    'target:': 'embouchure\xa0:',
    'lower fence:': 'clôture supérieure\xa0:',
    'upper fence:': 'clôture inférieure\xa0:',
    'max:': 'max.\xa0:',
    'mean ± σ:': 'moyenne ± σ\xa0:',
    'mean:': 'moyenne\xa0:',
    'median:': 'médiane\xa0:',
    'min:': 'min.\xa0:',
    'new text': 'nouveau texte',
    'Turntable rotation': 'Rotation planaire',
    'Toggle Spike Lines': 'Activer/désactiver les pics',
    'open:': 'ouverture\xa0:',
    'high:': 'haut\xa0:',
    'low:': 'bas\xa0:',
    'Toggle show closest data on hover': 'Activer/désactiver le survol',
    'incoming flow count:': 'flux entrant\xa0:',
    'outgoing flow count:': 'flux sortant\xa0:',
    'kde:': 'est. par noyau\xa0:'
  },
  format: {
    days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    shortDays: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
    months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
    shortMonths: ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'],
    date: '%d.%m.%Y',
    decimal: '.',
    thousands: "'",
    year: '%Y',
    month: '%b %Y',
    dayMonth: '%-d %b',
    dayMonthYear: '%-d %b %Y'
  }
})

export default {
  name: 'Plotly',
  /*
  ██████  ██████   ██████  ██████  ███████
  ██   ██ ██   ██ ██    ██ ██   ██ ██
  ██████  ██████  ██    ██ ██████  ███████
  ██      ██   ██ ██    ██ ██           ██
  ██      ██   ██  ██████  ██      ███████
  */
  props: {
    data: {
      type: Array,
      required: true
    },
    layout: {
      type: Object,
      required: true
    },
    mouseOverValue: {
      type: String,
      default: null
    },
    tracesStates: {
      type: Array,
      default () {
        return []
      }
    }
  },
  /*
  ██████   █████  ████████  █████
  ██   ██ ██   ██    ██    ██   ██
  ██   ██ ███████    ██    ███████
  ██   ██ ██   ██    ██    ██   ██
  ██████  ██   ██    ██    ██   ██
  */
  data () {
    return {
      locale: 'fr-CH',
      dataObject: _.map(this.data, data => data.toObject()),
      now: moment.utc(),
      realHoverValue: null
    }
  },
  /*
   ██████  ██████  ███    ███ ██████  ██    ██ ████████ ███████ ██████
  ██      ██    ██ ████  ████ ██   ██ ██    ██    ██    ██      ██   ██
  ██      ██    ██ ██ ████ ██ ██████  ██    ██    ██    █████   ██   ██
  ██      ██    ██ ██  ██  ██ ██      ██    ██    ██    ██      ██   ██
   ██████  ██████  ██      ██ ██       ██████     ██    ███████ ██████
  */
  computed: {
    layoutObject () {
      let layout = this.layout.toObject()

      if (layout._showHover && this.realHoverValue) {
        // NOTE shapes[0] réservé pour la ligne du hover
        layout.shapes[0] = {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: this.realHoverValue,
          y0: 0,
          x1: this.realHoverValue,
          y1: 1,
          line: {
            color: '#444444'
          }
        }
      } else {
        layout.shapes[0] = null
      }

      if (layout._displayNow) {
        // NOTE annotations[0] réservé pour le texte de now
        layout.annotations[0] = {
          xref: 'x',
          yref: 'paper',
          x: this.now.toISOString(true),
          y: 1,
          text: this.now.format(DISPLAY_DATE_HOUR),
          showarrow: false,
          xanchor: 'center',
          yanchor: 'bottom',
          font: {
            color: 'white'
          },
          bgcolor: '#0098ff'
        }

        // NOTE shapes[1] réservé pour le now
        layout.shapes[1] = {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: this.now.toISOString(true),
          y0: 0,
          x1: this.now.toISOString(true),
          y1: 1,
          line: {
            color: '#0098ff'
          }
        }
      }

      return layout
    }
  },
  /*
  ██     ██  █████  ████████  ██████ ██   ██
  ██     ██ ██   ██    ██    ██      ██   ██
  ██  █  ██ ███████    ██    ██      ███████
  ██ ███ ██ ██   ██    ██    ██      ██   ██
   ███ ███  ██   ██    ██     ██████ ██   ██
  */
  watch: {
    now () {
      if (this.layoutObject._displayNow) {
        this.relayout()
      }
    },
    realHoverValue () {
      this.relayoutShapes(this.layoutObject)
    },
    mouseOverValue () {
      if (this.mouseOverValue === null) {
        if (!this.mouseOverIgniter) {
          Plotly.Fx.hover(this.$el, [])
        }
        this.realHoverValue = null
        return
      }

      // Partie pour les valeurs non temporelles

      if (!this.layoutObject.xaxis._isDate && !this.layoutObject.yaxis._isDate) {
          if (!this.mouseOverIgniter) {
            Plotly.Fx.hover(this.$el, { xval: this.mouseOverValue })
          }
          return
      }

      // Partie pour les valeurs temporelles

      let momentValue = moment.utc(this.mouseOverValue)

      if (this.layoutObject.xaxis._isDate) {
        if (this.checkMouseHoverDate(momentValue)) {
          if (this.layoutObject.xaxis._noYear) {
            momentValue = moment.utc(this.mouseOverValue).year(2000)
          } else if (this.layoutObject.xaxis._noYearHydrological) {
            momentValue = moment.utc(this.mouseOverValue)
            momentValue = moment.utc(momentValue).year(momentValue.month() >= MONTH_START_OF_YEAR_HYDROLOGICAL - 1 ? 1999 : 2000)
          }

          if (!this.mouseOverIgniter) {
            Plotly.Fx.hover(this.$el, { xval: moment.utc(momentValue).valueOf() })
          }
          this.realHoverValue = momentValue.toISOString(true)
        } else {
          if (!this.mouseOverIgniter) {
            Plotly.Fx.hover(this.$el, [])
          }
          this.realHoverValue = null
        }
        return
      }

      if (this.layoutObject.yaxis._isDate) {
        if (this.checkMouseHoverDate(momentValue)) {
          if (this.layoutObject.yaxis._noYear) {
            momentValue = moment.utc(this.mouseOverValue).year(2000)
          } else if (this.layoutObject.xaxis._noYearHydrological) {
            momentValue = moment.utc(this.mouseOverValue)
            momentValue = moment.utc(momentValue).year(momentValue.month() >= MONTH_START_OF_YEAR_HYDROLOGICAL - 1 ? 1999 : 2000)
          }

          if (!this.mouseOverIgniter) {
            Plotly.Fx.hover(this.$el, { yval: moment.utc(momentValue).valueOf() })
          }
          this.realHoverValue = momentValue.toISOString(true)
        } else {
          if (!this.mouseOverIgniter) {
            Plotly.Fx.hover(this.$el, [])
          }
          this.realHoverValue = null
        }
        return
      }

      let hovers = []
      _.forEach(this.dataObject, (data, curveNumber) => {
        let pointNumber = _.findIndex(data._mouseOver, mouseOver => moment.utc(mouseOver).isSame(momentValue))
        if (pointNumber >= 0) {
          hovers.push({
            curveNumber,
            pointNumber
          })
        }
      })
      Plotly.Fx.hover(this.$el, hovers)
      this.realHoverValue = momentValue.toISOString(true)
    },
    tracesStates () {
      _.forEach(this.dataObject, (data, index) => {
        data.visible = this.tracesStates[index]
      })
      this.relayout()
    }
  },
  /*
  ██████  ███████ ███████  ██████  ██████  ███████     ██████  ███████ ███████ ████████ ██████   ██████  ██    ██
  ██   ██ ██      ██      ██    ██ ██   ██ ██          ██   ██ ██      ██         ██    ██   ██ ██    ██  ██  ██
  ██████  █████   █████   ██    ██ ██████  █████       ██   ██ █████   ███████    ██    ██████  ██    ██   ████
  ██   ██ ██      ██      ██    ██ ██   ██ ██          ██   ██ ██           ██    ██    ██   ██ ██    ██    ██
  ██████  ███████ ██       ██████  ██   ██ ███████     ██████  ███████ ███████    ██    ██   ██  ██████     ██
  */
  beforeDestroy () {
    clearTimeout(this.nowUpdaterTimeout)
    clearInterval(this.nowUpdaterInterval)
    this.$el.removeAllListeners('plotly_hover')
    this.$el.removeAllListeners('plotly_unhover')
    this.$el.removeAllListeners('plotly_restyle')
    this.$el.removeAllListeners('plotly_relayout')
    Plotly.purge(this.$el)
  },
  /*
  ███    ███  ██████  ██    ██ ███    ██ ████████ ███████ ██████
  ████  ████ ██    ██ ██    ██ ████   ██    ██    ██      ██   ██
  ██ ████ ██ ██    ██ ██    ██ ██ ██  ██    ██    █████   ██   ██
  ██  ██  ██ ██    ██ ██    ██ ██  ██ ██    ██    ██      ██   ██
  ██      ██  ██████   ██████  ██   ████    ██    ███████ ██████
  */
  mounted () {
    Plotly.newPlot(this.$el, this.dataObject, this.layoutObject, {
      locale: this.locale,
      responsive: true,
      // displaylogo: false,
      modeBarButtonsToRemove: [
        // 'zoom2d',
        // 'pan2d',
        'select2d',
        'lasso2d',
        'zoomIn2d',
        'zoomOut2d',
        'autoScale2d',
        // 'resetScale2d',
        'hoverClosestCartesian',
        'hoverCompareCartesian',
        // 'zoom3d',
        // 'pan3d',
        // 'resetCameraDefault3d',
        'resetCameraLastSave3d',
        'hoverClosest3d',
        // 'orbitRotation',
        // 'tableRotation',
        // 'zoomInGeo',
        // 'zoomOutGeo',
        // 'resetGeo',
        'hoverClosestGeo',
        // 'toImage',
        'sendDataToCloud',
        'hoverClosestGl2d',
        'hoverClosestPie',
        'toggleHover',
        // 'resetViews',
        'toggleSpikelines'
        // 'resetViewMapbox'
      ]
    })

    this.$el.on('plotly_hover', event => {
      this.mouseOverIgniter = true
      this.$emit('mouse-over', _.head(event.points))
    })

    this.$el.on('plotly_unhover', () => {
      this.mouseOverIgniter = false
      this.$emit('mouse-out')
    })

    this.$el.on('plotly_restyle', () => {
      let tracesStates = []
      _.forEach(this.dataObject, data => {
        tracesStates.push(data.visible)
      })
      this.$emit('update-traces', tracesStates)
    })

    this.$el.on('plotly_relayout', event => {
      if (event['xaxis.range[0]'] && event['xaxis.range[1]']) {
        this.$emit('zoom-x', {
          begin: event['xaxis.range[0]'],
          end: event['xaxis.range[1]']
        })
      }
    })

    // Mise en place du timer toutes les minutes dès la prochaine minute plaine
    this.nowUpdaterTimeout = setTimeout(this.updateNowInterval, moment.utc().add(1, 'minute').startOf('minute').diff(moment.utc()))
  },
  /*
  ███    ███ ███████ ████████ ██   ██  ██████  ██████  ███████
  ████  ████ ██         ██    ██   ██ ██    ██ ██   ██ ██
  ██ ████ ██ █████      ██    ███████ ██    ██ ██   ██ ███████
  ██  ██  ██ ██         ██    ██   ██ ██    ██ ██   ██      ██
  ██      ██ ███████    ██    ██   ██  ██████  ██████  ███████
  */
  methods: {
    checkMouseHoverDate () {
      // return _.compact(_.map(this.dataObject, data => data.visible === true && _.includes(data._mouseOver, date.toISOString(true)))).length > 0
      return _.compact(_.map(this.dataObject, data => data.visible === true)).length > 0
    },
    relayout () {
      Plotly.relayout(this.$el, this.layoutObject)
    },
    react () {
      Plotly.react(this.$el, _.map(this.data, data => data.toObject()), this.layoutObject)
    },
    relayoutShapes (layout) {
      Plotly.relayout(this.$el, {
        shapes: layout.shapes
      })
    },
    updateNowInterval () {
      this.updateNow()
      this.nowUpdaterInterval = setInterval(this.updateNow, 1000 * 60)
    },
    updateNow () {
      this.now = moment.utc()
    }
  }
}
</script>

<style scoped lang="sass">
.plotly
  user-select: none
</style>
